import React from 'react';

/**
 * Design spec (single source of truth for buttons):
 * - 1px borders everywhere except `danger-outline` (2px, reserved for
 *   destructive secondary actions alongside selected OptionList rows).
 * - `rounded-lg` corners; `gap-2` handles icon spacing (no mr-* on icons).
 * - Focus is `focus-visible` only: ring-2 brand-400 + offset (see
 *   VersionChecker precedent). Mouse clicks show no ring.
 * - duration-150: sideline taps should feel instant, not sluggish.
 * - Primary is brand-600 (not 500) so white text holds up in sunlight.
 * - `dark` is the scoreboard treatment: reserved for the one hero action
 *   that must never be confused with Start/Pause/End (the Goal button).
 * - `outlineOnDark` is the ghost treatment for light text on dark/photographic
 *   backgrounds. Never layer light-text overrides on top of the light `outline`
 *   variant — plain class concatenation can't resolve the conflict and the text
 *   can end up invisible until hover.
 */
const variants = {
  primary: 'bg-brand-600 hover:bg-brand-700 text-white border border-brand-600 hover:border-brand-700',
  dark: 'bg-gray-900 hover:bg-gray-800 text-white border border-gray-900 hover:border-gray-800 dark:bg-gray-100 dark:hover:bg-white dark:text-gray-900 dark:border-gray-100 dark:hover:border-white',
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200 hover:border-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 dark:border-gray-600 dark:hover:border-gray-500',
  success: 'bg-green-600 hover:bg-green-700 text-white border border-green-600 hover:border-green-700',
  danger: 'bg-red-600 hover:bg-red-700 text-white border border-red-600 hover:border-red-700',
  warning: 'bg-yellow-500 hover:bg-yellow-600 text-white border border-yellow-500 hover:border-yellow-600',
  outline: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 hover:border-gray-400 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:border-gray-500',
  outlineOnDark: 'bg-transparent hover:bg-white/10 text-white border border-white/40 hover:border-white/70',
  'danger-outline': 'bg-white hover:bg-red-50 text-red-600 border-2 border-red-300 hover:border-red-400 dark:bg-gray-800 dark:hover:bg-red-950/30 dark:text-red-400 dark:border-red-800 dark:hover:border-red-600',
  link: 'bg-transparent border border-transparent text-brand-700 hover:text-brand-800 hover:underline dark:text-brand-400 dark:hover:text-brand-300'
};

const sizes = {
  small: 'px-3 py-2 text-sm',
  medium: 'px-4 py-2 text-base',
  large: 'px-6 py-3 text-lg'
};

export function Button({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
  type = 'button',
  ...props
}) {
  const baseClasses = 'font-medium rounded-lg transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2';
  const variantClasses = variants[variant] || variants.primary;
  const sizeClasses = sizes[size] || sizes.medium;
  const widthClasses = fullWidth ? 'w-full' : '';
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={`${baseClasses} ${variantClasses} ${sizeClasses} ${widthClasses} ${className}`}
      {...props}
    >
      {loading && (
        <span
          className="animate-spin rounded-full border-2 border-current border-t-transparent h-4 w-4 shrink-0"
          aria-hidden
        />
      )}
      {children}
    </button>
  );
}

export default Button;
